import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const UserModal = ({ isOpen, onClose, onSubmit, user = null }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: 'user',
    company: '',
    password: '',
    permissions: {
      invoices: { view: false, create: false, edit: false, delete: false },
      expenses: { view: false, create: false, edit: false, delete: false },
      reports: { view: false, create: false, edit: false, delete: false },
      customers: { view: false, create: false, edit: false, delete: false },
      settings: { view: false, create: false, edit: false, delete: false }
    }
  });

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user?.fullName || '',
        email: user?.email || '',
        role: user?.role || 'user',
        company: user?.company || '',
        password: '',
        permissions: user?.permissions || {
          invoices: { view: false, create: false, edit: false, delete: false },
          expenses: { view: false, create: false, edit: false, delete: false },
          reports: { view: false, create: false, edit: false, delete: false },
          customers: { view: false, create: false, edit: false, delete: false },
          settings: { view: false, create: false, edit: false, delete: false }
        }
      });
    } else {
      setFormData({
        fullName: '',
        email: '',
        role: 'user',
        company: '',
        password: '',
        permissions: {
          invoices: { view: false, create: false, edit: false, delete: false },
          expenses: { view: false, create: false, edit: false, delete: false },
          reports: { view: false, create: false, edit: false, delete: false },
          customers: { view: false, create: false, edit: false, delete: false },
          settings: { view: false, create: false, edit: false, delete: false }
        }
      });
    }
  }, [user, isOpen]);

  const roleOptions = [
    { value: 'admin', label: 'Administrator - Full system access' },
    { value: 'accountant', label: 'Accountant - Manage transactions' },
    { value: 'manager', label: 'Manager - View and approve' },
    { value: 'user', label: 'User - Basic access' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData, user?.id);
    onClose();
  };

  const handlePermissionChange = (module, action, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev?.permissions,
        [module]: {
          ...prev?.permissions?.[module],
          [action]: checked
        }
      }
    }));
  };

  const handleSelectAllModule = (module, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev?.permissions,
        [module]: {
          view: checked,
          create: checked,
          edit: checked,
          delete: checked
        }
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            {user ? 'Edit User' : 'Add New User'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            {/* User Information */}
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">User Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  value={formData?.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
                  required
                  placeholder="Enter full name"
                />

                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  required
                  placeholder="user@example.com"
                  disabled={!!user}
                />

                <Input
                  label="Company"
                  value={formData?.company}
                  onChange={(e) => setFormData({ ...formData, company: e?.target?.value })}
                  placeholder="Company name"
                />

                {!user && (
                  <Input
                    label="Password"
                    type="password"
                    value={formData?.password}
                    onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                    placeholder="Enter password"
                    description="Leave blank to auto-generate"
                  />
                )}
              </div>
            </div>

            {/* Role Assignment */}
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Role Assignment</h3>
              <Select
                label="User Role"
                options={roleOptions}
                value={formData?.role}
                onChange={(value) => setFormData({ ...formData, role: value })}
                required
              />
            </div>

            {/* Permissions */}
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Module Permissions</h3>
              <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                {Object.keys(formData?.permissions)?.map((module) => (
                  <div key={module} className="border-b border-border pb-4 last:border-b-0 last:pb-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon name="Shield" size={16} className="text-primary" />
                        <span className="font-medium text-sm text-foreground capitalize">{module}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => handleSelectAllModule(module, !formData?.permissions?.[module]?.view)}
                      >
                        Select All
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['view', 'create', 'edit', 'delete']?.map((action) => (
                        <label key={action} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData?.permissions?.[module]?.[action]}
                            onChange={(e) => handlePermissionChange(module, action, e?.target?.checked)}
                          />
                          <span className="text-sm text-foreground capitalize">{action}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              fullWidth
              disabled={!formData?.fullName || !formData?.email}
            >
              {user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;